// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { CameraSource } from "@capacitor/camera";
import { useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack, ThemeProvider } from "@mui/material";
import { useUpdateProfilePicture } from "@src/appV2/Agents/api/useUpdateProfilePicture";
import {
  resetAgentProfilePictureQuery,
  useWorkerProfilePicture,
} from "@src/appV2/Agents/api/useWorkerProfilePicture";
import { useToast } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { type CameraPermissionDeniedError } from "@src/appV2/lib/Media/CameraPermissionDeniedError";
import { type UploadFileData } from "@src/appV2/lib/Media/types";
import { useBrowserOnFileChange } from "@src/appV2/lib/Media/useBrowserOnFileChange";
import { useMobileOnFileSelect } from "@src/appV2/lib/Media/useMobileOnFileSelect";
import { useTakePhoto } from "@src/appV2/lib/Media/useTakePhoto";
import { isCapacitorPlatform } from "@src/appV2/lib/utils/capacitor/platformUtils";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { PROFILE_FULL_PATH } from "../../Accounts/paths";
import { Button } from "../../components/Button";
import { FileUploaderBottomSheet } from "../../components/FileUploaderBottomSheet";
import { PageWrapper } from "../../components/PageWrapper";
import { StickyScrollPageHeader } from "../../components/StickyScrollPageHeader";
import { PageContent } from "../../WorkerShifts/components/PageContent";
import { ImageCropperModal } from "./ImageCropperModal";
import { ProfileImageSection } from "./ProfileImageSection";

export function UpdateProfileImagePage() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const history = useHistory();
  const queryClient = useQueryClient();
  const worker = useDefinedWorker();
  const { showSuccessToast, showErrorToast } = useToast();
  useLogEffect(APP_V2_USER_EVENTS.PROFILE_UPDATE_PROFILE_IMAGE_INITIATED);

  const [selectedImage, setSelectedImage] = useState<{
    dataUrl: string;
    blob: Blob;
  }>();

  const fileUploaderModalState = useModalState();
  const imageCropperModalState = useModalState();

  const { data: workerProfileImageUrl, isLoading: isLoadingProfileImage } = useWorkerProfilePicture(
    worker.userId
  );

  const { mutateAsync: updateProfilePicture, isLoading: isUploading } = useUpdateProfilePicture({
    onSuccess: () => {
      resetAgentProfilePictureQuery(queryClient, { workerId: worker.userId });
      showSuccessToast("Profile photo updated successfully");
      logEvent(APP_V2_USER_EVENTS.PROFILE_UPDATE_PROFILE_IMAGE_COMPLETED, {
        workerId: worker.userId,
      });
      history.push(PROFILE_FULL_PATH);
    },
    onError: (error: unknown) => {
      showErrorToast("Failed to update profile photo. Please try again.");
      logError(APP_V2_APP_EVENTS.UPDATE_AGENT_PROFILE_PICTURE_FAILURE, {
        error,
        metadata: { workerId: worker.userId },
      });
    },
  });

  const handlePhotoSelected = useCallback(
    (photo: UploadFileData) => {
      setSelectedImage({
        dataUrl: photo.dataUrlBase64,
        blob: photo.blob,
      });
      fileUploaderModalState.closeModal();
      imageCropperModalState.openModal();
    },
    [fileUploaderModalState, imageCropperModalState]
  );

  const handleError = useCallback(
    (error: Error | CameraPermissionDeniedError) => {
      showErrorToast("Failed to select photo. Please try again.");
      logError(APP_V2_USER_EVENTS.PROFILE_UPDATE_PROFILE_IMAGE_SELECTION_FAILURE, {
        error,
        metadata: { workerId: worker.userId },
      });
    },
    [showErrorToast, worker.userId]
  );

  const handleTakePhoto = useTakePhoto({
    source: CameraSource.Camera,
    onSuccess: handlePhotoSelected,
    onError: handleError,
  });

  const handleSelectPhotoFromCameraRoll = useTakePhoto({
    source: CameraSource.Photos,
    onSuccess: handlePhotoSelected,
    onError: handleError,
  });

  const handleSelectFile = useMobileOnFileSelect({
    onSuccess: handlePhotoSelected,
    onError: handleError,
  });

  const browserOnFileChange = useBrowserOnFileChange({
    onSuccess: handlePhotoSelected,
    onError: handleError,
  });

  const handleBrowserSelectFile = useCallback(() => {
    inputFileRef.current?.click();
  }, []);

  const handleUploadPhoto = useCallback(() => {
    if (isCapacitorPlatform()) {
      fileUploaderModalState.openModal();
    } else {
      handleBrowserSelectFile();
    }
  }, [fileUploaderModalState, handleBrowserSelectFile]);

  const handleCropComplete = useCallback(
    async (croppedBlob: Blob) => {
      imageCropperModalState.closeModal();

      const file = new File([croppedBlob], "profile-image.jpg", {
        type: "image/jpeg",
      });

      await updateProfilePicture({
        file,
        workerId: worker.userId,
      });
    },
    [imageCropperModalState, updateProfilePicture, worker.userId]
  );

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <StickyScrollPageHeader
          title="Update Profile Image"
          scrollContainerRef={scrollRef}
          fallbackGoBackPath={PROFILE_FULL_PATH}
          headerVariant="tertiary"
        />

        <PageContent ref={scrollRef}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              paddingY: 12,
              paddingX: 5,
            }}
          >
            <Box width="100%" maxWidth={600}>
              <Stack spacing={8} alignItems="center">
                <ProfileImageSection
                  firstName={worker.firstName ?? ""}
                  lastName={worker.lastName ?? ""}
                  profileImageUrl={workerProfileImageUrl?.distribution_url}
                  isLoadingProfileImage={isLoadingProfileImage}
                  isUploading={isUploading}
                />

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  startIconType="pencil"
                  disabled={isUploading || isLoadingProfileImage}
                  data-testid={workerProfileImageUrl?.distribution_url ?? "confirm-upload-photo"}
                  onClick={handleUploadPhoto}
                >
                  {workerProfileImageUrl?.distribution_url ? "Change Photo" : "Upload Photo"}
                </Button>
              </Stack>
            </Box>
          </Box>
        </PageContent>

        {/* Mobile file upload options */}
        <FileUploaderBottomSheet
          modalState={fileUploaderModalState}
          onTakePhoto={handleTakePhoto}
          onSelectPhotoFromCameraRoll={handleSelectPhotoFromCameraRoll}
          onSelectFile={handleSelectFile}
        />

        {/* Image cropper modal */}
        {isDefined(selectedImage) && (
          <ImageCropperModal
            modalState={imageCropperModalState}
            imageDataUrl={selectedImage.dataUrl}
            isUploading={isUploading}
            onCropComplete={handleCropComplete}
            onCancel={() => {
              imageCropperModalState.closeModal();
              setSelectedImage(undefined);
            }}
          />
        )}

        {/* Hidden file input for browser */}
        <input
          ref={inputFileRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(event) => {
            void browserOnFileChange(event);
          }}
        />
      </PageWrapper>
    </ThemeProvider>
  );
}
